import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { isApiError } from '../api/client';
import { useApi } from '../api/context';
import type { Merchant, PanelStatus } from '../api/types';

/**
 * The signed-in merchant (GET /merchants/me) and the web panel subscription (GET
 * /merchants/me/panel — reading it also lets the API bill a new month, like ZINQ Plus).
 * 404 MERCHANT_NOT_FOUND = the user has no business profile yet (set it up in the app first).
 */
export type MerchantState =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'error'; error: unknown }
  | { status: 'ready'; merchant: Merchant };

export interface MerchantContextValue {
  state: MerchantState;
  /** null = not known (still loading or the panel status could not be read). */
  panel: PanelStatus | null;
  panelError: unknown;
  reload: () => void;
  refreshPanel: () => Promise<PanelStatus | null>;
  setPanel: (panel: PanelStatus) => void;
}

const MerchantContext = createContext<MerchantContextValue | null>(null);

export function MerchantProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [state, setState] = useState<MerchantState>({ status: 'loading' });
  const [panel, setPanel] = useState<PanelStatus | null>(null);
  const [panelError, setPanelError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void Promise.allSettled([api.merchant(), api.panel()]).then(([m, p]) => {
      if (cancelled) return;
      if (p.status === 'fulfilled') {
        setPanel(p.value);
        setPanelError(null);
      } else {
        setPanel(null);
        setPanelError(p.reason);
      }
      if (m.status === 'fulfilled') setState({ status: 'ready', merchant: m.value });
      else if (isApiError(m.reason, 'MERCHANT_NOT_FOUND') || (isApiError(m.reason) && m.reason.status === 404)) {
        setState({ status: 'none' });
      } else setState({ status: 'error', error: m.reason });
    });
    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const refreshPanel = useCallback(async () => {
    try {
      const next = await api.panel();
      setPanel(next);
      setPanelError(null);
      return next;
    } catch (e) {
      setPanelError(e);
      throw e;
    }
  }, [api]);

  const value = useMemo<MerchantContextValue>(
    () => ({ state, panel, panelError, reload, refreshPanel, setPanel }),
    [state, panel, panelError, reload, refreshPanel],
  );
  return <MerchantContext.Provider value={value}>{children}</MerchantContext.Provider>;
}

export function useMerchantState(): MerchantContextValue {
  const ctx = useContext(MerchantContext);
  if (!ctx) throw new Error('useMerchantState outside <MerchantProvider>');
  return ctx;
}

/** The merchant inside the signed-in layout (only rendered once it is loaded). */
export function useMerchant(): Merchant {
  const { state } = useMerchantState();
  if (state.status !== 'ready') throw new Error('useMerchant before the merchant is loaded');
  return state.merchant;
}

/** The panel subscription when known (null outside the signed-in area or while unknown). */
export function usePanel(): PanelStatus | null {
  return useContext(MerchantContext)?.panel ?? null;
}

/** true / false when known; null while the panel status is unknown. */
export function usePanelActive(): boolean | null {
  const panel = usePanel();
  return panel ? panel.active : null;
}
